import { Component, computed, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserModel } from '../../../../core/models/data/user.model';
import { StaffRoleAssignment } from '../../../../core/models/ui/staff-role-assignment.model';
import { StaffRoleOption } from '../../../../core/models/ui/staff-role-option.model';
import { UserService } from '../../../../core/services/user.service';
import { UsersFilter } from '../../../../core/filters/users.filter';
import { UserListSortOptions } from '../../../../core/enums/sort-options.enums';
import { PlatformRoles, UserStatuses } from '../../../../core/enums/data.enums';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { StaffManagementMessagingConstants } from '../../../../core/constants/messaging.constants';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

/**
 * Picks a user and then a role for them, in that order, as two steps of one modal.
 *
 * The order matters for how this grows. Choosing the role first — whether as two entry buttons or
 * a selector above the list — makes the role a mode: every row's button does something the control
 * above it decided, which is easy to misread and gets worse, not better, as roles are added.
 * Choosing the person first means the role question is asked once, about someone specific, with the
 * whole dialog to itself — so a list of twenty roles with descriptions is simply a longer step, not
 * a longer menu on every row.
 *
 * Replacing the list with the role step (rather than revealing it below) is also what keeps this to
 * a single scrollbar: the modal body scrolls, nothing inside it does.
 */
@Component({
  selector: 'ms-staff-user-picker',
  imports: [CommonModule, FormsModule, FontAwesomeModule, AvatarComponent, PaginationComponent, TooltipDirective],
  templateUrl: './staff-user-picker.component.html',
  styleUrl: './staff-user-picker.component.scss'
})
export class StaffUserPickerComponent extends BaseListPageClass {
  private userService = inject(UserService);

  // The roles the viewer may grant, most permissive first, each with the API's own description.
  public roleOptions = input<StaffRoleOption[]>([]);

  // Which step to show, owned by the page: its presence puts the modal on the role step. Held
  // there rather than here so the modal footer, the role list and this component all read one
  // source instead of reaching into each other.
  public selectedUser = input<UserModel | undefined>(undefined);

  public userChosen = output<UserModel>();
  public roleSelected = output<StaffRoleAssignment>();

  public users = signal<UserModel[]>([]);
  public searchTerm = signal<string>('');
  public errorMessage = signal<string | undefined>(undefined);

  public StaffManagementMessagingConstants = StaffManagementMessagingConstants;

  // Reached when the chosen user already holds every role this viewer may grant, which the list
  // can now offer because it no longer filters out existing staff.
  public allRolesHeldMessage = computed<string>(() =>
    StaffManagementMessagingConstants.STAFF_ALL_ROLES_HELD()
      .replaceValuesWithObject({ firstName: this.selectedUser()?.firstName }));

  public avatarSize = AvatarConstants.SIZE().USER_LIST;
  public avatarShape = AvatarConstants.SHAPE().USER_LIST;
  public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().USER_LIST;

  public skeletonUsers: UserModel[] = [];

  @ViewChild('pickerTop') pickerTopRef?: ElementRef;

  constructor() {
    super();
    this.orderBy = UserListSortOptions.NameASC;
    this.createSkeletonUsers();
  }

  private createSkeletonUsers(): void {
    this.skeletonUsers = Array.from({ length: this.take }, (_, index) => ({
      userId: index,
      firstName: 'Loading',
      lastName: 'User',
      emailAddress: 'loading@example.com',
      status: UserStatuses.Active,
      lastLogin: new Date()
    } as UserModel));
  }

  // Skeleton rows stand in while a page is in flight. Without them ms-pagination reads an empty
  // list with hasNext still false and prints "No results found" over a request that hasn't landed.
  public get displayUsers(): UserModel[] {
    const loaded = this.users();
    return (this.isLoading && loaded.length === 0) ? this.skeletonUsers : loaded;
  }

  // Called each time the modal is opened. Clears the previous visit so the list can't look like it
  // was never closed, and so a user granted a role on the last visit isn't still offered.
  public open(): void {
    this.searchTerm.set('');
    this.errorMessage.set(undefined);
    this.users.set([]);
    this.loadUsers();
  }

  public async loadUsers(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    // Every active user, including those who already hold a staff role: a user may be given more
    // than one, so filtering them out would make a second role impossible to grant. The role step
    // is where that is narrowed — it offers only roles this particular user does not already have.
    // (hasRole: false was the old filter. It is all-or-nothing, which is why it had to go; the
    // Authentication API has no "users who do not hold role X" filter to replace it with.)
    const filter: UsersFilter = {
      searchTerm: this.searchTerm(),
      statuses: [UserStatuses.Active]
    };

    const response = await this.userService.searchUsers(filter, this.orderBy, this.skip(), this.take);

    // A failed search reports itself through success, not an exception. Applying its empty payload
    // would render "No results found" over a request that actually errored.
    if (response.success === false) {
      this.errorMessage.set(response.message || StaffManagementMessagingConstants.STAFF_ERROR_LOADING_USERS());
      this.isLoading = false;
      return;
    }

    this.errorMessage.set(undefined);
    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    const page = response?.data?.data ?? [];
    this.users.set(append ? this.users().concat(page) : page);

    this.isLoading = false;
  }

  public clearSearch(): void {
    this.searchTerm.set('');
    this.loadUsers();
  }

  public onUserClicked(user: UserModel): void {
    this.userChosen.emit(user);
  }

  public onRoleClicked(role: PlatformRoles): void {
    const user = this.selectedUser();
    if (!user) return;

    this.roleSelected.emit({ user: user, role: role });
  }

  // The picker scrolls inside the modal body, so both of ms-pagination's jump targets resolve to
  // the one anchor at the top of this list.
  public jumpToTop(): void {
    this.pickerTopRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadUsers(true);
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
}
